import { Field, Form, Formik } from "formik";
import React, { FC } from "react";
import Modal from 'react-modal';
import { useLocale } from "../../../contexts/localeContext";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import closeIcon from '../../../images/close.svg';
import { requiredValidation } from "../../../utilities/Validation";
import Label from "../../label/label";
import { LOGIN_STATUS } from "../../../constants/user-login-constants";
import { numbers } from "../../../utilities/regex";

interface MfaModalProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    onSubmitMfa: (values: any) => void;
    mfaValues: any
    qrCodeImage?: string;
}

/**
 * This modal is used when a user attempts to login and has MFA enabled.
 * Depending on what is required, we may show a QR Code to register with their authentication app 
 * and/or ask them to enter their authentication code.
 * 
 * Submitting this modal will result in another login attempt.
 * @param param0 
 */
const MfaModal: FC<MfaModalProps> = ({ isOpen, setIsOpen, onSubmitMfa, qrCodeImage, mfaValues }) => {
    const { translate } = useLocale();
    const initialValues = {
        status: LOGIN_STATUS.MFA_CODE,
        qrCodeImage: '',
        username: '',
        password: '',
        authCode: ''
    }

    const doSubmit = (fields) => {
        const fieldsToSubmit = {
            remember: false,
            username: mfaValues.username,
            password: mfaValues.password,
            authCode: fields.authCode,
            status: mfaValues.status
        };

        onSubmitMfa(fieldsToSubmit);
    }

    const validateAuthCode = event => {
      const key = event.key;
      const isDigit = numbers.test(key);

      // Always allow Backspace
      if (key === 'Backspace') {
        return;
      }

      // Max 6-digit code and digits only
      if (event.target.value.length >= 6 || !isDigit) {
        event.preventDefault();
      }
    };

    return <Modal
        isOpen={isOpen}
        ariaHideApp={false}
        overlayClassName="flex w-screen position-float fixed top-0 left-0 h-screen bg-opacity-75 bg-white"
        className="bg-white border-none m-auto shadow select-none outline-none overflow-hidden">
        <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            onSubmit={doSubmit}>
            {
                ({ errors }) => {
                    return <Form>

                        {/* Title */}
                        <div className={`w-full p-2 border-b flex flex-row items-center bg-sidemenu-focus text-white justify-between`}>
                            <div className="flex flex-row">
                                <p className="pl-3 font-bold font text-white">{translate.byKey(TranslationKeys.mfa)}</p>
                            </div>
                            <img src={closeIcon} onClick={() => setIsOpen(false)} className="cursor-pointer mr-2"/>
                        </div>

                        {/* Content */}
                        <div className="pt-3 pl-4 pr-0" style={{ width: '380px' }}>

                            {/* Scan QR Code */}
                            {mfaValues.status === LOGIN_STATUS.MFA_REGISTRATION ? (
                                <div className="flex flex-col flex-1 pr-3">
                                    <span className='font-bold pt-3 text-portal-blue-primary'>{translate.byKey(TranslationKeys.scanQrCodeForAuthenticatorApp)}:</span>
                                    <div className="flex flex-row justify-between">
                                        <img className="h-18 w-18 " src={qrCodeImage} alt="" />
                                    </div>
                                </div>)
                                : null}

                            {/* Enter authentication code */}
                            <span className='font-bold pt-3 text-portal-blue-primary'>{translate.byKey(TranslationKeys.enterAuthenticatorCode)}:</span>
                            <div className="flex flex-col flex-1 pr-3 mt-2">
                                <div className="flex flex-row justify-between">
                                    <Label htmlFor='authCode' className="text-xs text-gray-600 mt-1" required>
                                        {translate.byKey(TranslationKeys.authenticationCode)}:
                                    </Label>
                                    {errors.authCode ? <span className='text-red-600 text-xs'>{translate.byKey(TranslationKeys.required)}</span> : null}
                                </div>
                                <Field
                                    name="authCode"
                                    type="password"
                                    onKeyDown={(event) => validateAuthCode(event)}
                                    validate={(value) => requiredValidation(value, translate)}
                                    className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm bg-gray-input w-full"/>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex flex-row pb-3 justify-end bg-white">
                            <div className="flex flex-row mt-4 mr-5">
                                <button id='submitBtn' className="secondary secondary-enabled py-1 px-5" type='submit'>
                                    {translate.byKey(TranslationKeys.verify)}
                                </button>
                            </div>
                        </div>
                    </Form>
                }
            }
        </Formik>
    </Modal>
}

export default MfaModal;
